<template>
  <ValidationObserver v-slot="{ invalid }">
    <div class="vx-row">
      <!------------------------------------------ Left Side ---------------------------------------------------------->
      <div class="vx-col sm:w-2/5 w-full mi-border-right">

        <div class="vx-row">
          <div class="vx-col w-1/2 pt-1">
            <h5> {{ $t('vdo-set.vdo-set-topic') }} </h5>
          </div>

          <div class="vx-col w-1/2 pr-5">
            <vx-tooltip :text="$t('general.add') + $t('vdo-set.vdo-set-topic')" class="float-right">
              <vs-button :disabled="isUploading" color="dark" icon="icon-plus" icon-pack="feather" radius size="small"
                         type="border" @click="onAddClick">
              </vs-button>
            </vx-tooltip>
          </div>

        </div>

        <div class="vs-row">
          <VuePerfectScrollbar :settings="settings">
            <div class="vx-col w-full" style="height: 500px;">
              <vs-list>
                <vs-list-item v-for="(item, index) in vdoSetTopics" :key="index"
                              :class="setSelectedClass(item)"
                              :icon="item.vdoLength > 0 ? 'icon-video':'icon-video-off'"
                              :subtitle="setVdoLengthLabel(item.vdoLength)"
                              :title="item.no + '. ' +item.nameLocal"
                              class="hover:text-primary pr-1"
                              icon-pack="feather">


                  <div class="flex">
                    <vx-tooltip v-if="!item.isEnabled"
                                :text="$t('vdo-set-topic.disabled')">
                      <feather-icon class="mr-1" icon="XIcon"
                                    svgClasses="w-5 h-5 text-danger stroke-current"/>
                    </vx-tooltip>
                    <vx-tooltip v-if="canPreview(item)"
                                :text="$t('vdo-set-topic.preview')">
                      <feather-icon class="cursor-pointer mr-1" icon="PlayIcon"
                                    svgClasses="w-5 h-5 hover:text-primary stroke-current"
                                    @click="onPreviewVideoClick(item)"/>
                    </vx-tooltip>
                    <!-- Gated on either provider's marker, and routed through
                         onDownloadClick rather than straight at the Wowza
                         handler: a Bunny topic may keep a stale vdoStreamPath
                         (see the comment on the Video tab below), so calling
                         onDownloadVideoClick here would hand back the old
                         Wowza file instead of the current Bunny one, and a
                         Bunny-only topic would show no download icon at all.
                         showDownload currently forces this off for both
                         providers -- see isVideoDownloadEnabled. -->
                    <vx-tooltip v-if="showDownload && (item.vdoStreamPath || item.bunnyVideoGuid)"
                                :text="$t('vdo-set-topic.download-vdo')">
                      <feather-icon class="cursor-pointer mr-1" icon="DownloadIcon"
                                    svgClasses="w-5 h-5 hover:text-primary stroke-current"
                                    @click="onDownloadClick(item)"/>
                    </vx-tooltip>
                    <vx-tooltip :text="$t('general.edit') + $t('vdo-set.vdo-set-topic')">
                      <feather-icon class="cursor-pointer" icon="EditIcon"
                                    svgClasses="w-5 h-5 hover:text-primary stroke-current"
                                    @click="onEditClick(item)"/>
                    </vx-tooltip>
                  </div>

                </vs-list-item>
              </vs-list>
              <vs-alert :active="showWarningNoTopics" color="warning" icon="icon-alert-triangle" icon-pack="feather">
                {{ $t('vdo-set-topic.no-vdo-set-topic') }}
              </vs-alert>
            </div>
          </VuePerfectScrollbar>
        </div>

      </div>

      <!------------------------------------------ Right Side --------------------------------------------------------->
      <div class="vx-col sm:w-3/5 w-full">
        <!-- PREVIEW VIDEO WHEN CLICK FROM LEFT SIDE-->
        <template v-if="currentVdoSetTopic && showPreviewVideo">
          <div class="vs-row">
            <div class="vx-col w-full mb-2">
              <div class="flex justify-between align-items-center">
                <h5 class="pt-2"> {{ $t('vdo-set-topic.preview-video-title') }} </h5>

                <vx-tooltip :text="$t('vdo-set-topic.stop-preview')">
                  <vs-button :disabled="local.isProcessing || isUploading"
                             color="danger"
                             radius type="border"
                             icon="close"
                             size="small"
                             @click.prevent="onTriggerPreviewClick(false)">
                  </vs-button>
                </vx-tooltip>
              </div>
            </div>
          </div>

          <vs-divider/>

          <div class="vx-row">
            <div class="vx-col w-full mb-2">
              <preview-video-card :current-vdo-set-topic="currentVdoSetTopic"></preview-video-card>
            </div>
          </div>
        </template>
        <!-- UPLOAD LEARN TIME FORM -->
        <template v-else-if="showUploadLearnTime && currentVdoSetTopic">
          <div class="vs-row">
            <div class="vx-col w-full mb-2">
              <div class="flex justify-between align-items-center">
                <h5 class="pt-2"> {{ $t('vdo-set-topic.upload-learn-time') }} </h5>

                <vx-tooltip :text="$t('general.close')">
                  <vs-button :disabled="local.isProcessing || isUploading"
                             color="danger"
                             radius type="border"
                             icon="close"
                             size="small"
                             @click.prevent="closeUploadLearnTimeCard">
                  </vs-button>
                </vx-tooltip>
              </div>
            </div>
          </div>

          <vs-divider/>
          <div class="vs-row">
            <div class="vx-col w-full mb-2">
              <upload-learn-time-card
                :vdo-set="vdoSetInfo.selectedVdoSet"
                :vdo-set-unit="vdoSetUnitEdit.selectedVdoSetUnit"
                :vdo-set-topic="currentVdoSetTopic"
              >
              </upload-learn-time-card>
            </div>
          </div>
        </template>

        <!-- EDIT FORM-->
        <template v-else>

          <div class="vs-row mb-2">
            <div class="vx-col w-full">
              <h5> {{ vdoSetTopicFormHeader }} </h5>
            </div>
          </div>

          <vs-divider/>

          <vs-tabs v-model="selectedTab">
            <!---------------------- TOPIC DETAIL ------------------------>
            <vs-tab :label="$t('vdo-set-topic.topic-detail')">

              <div class="vx-row mb-2">
                <div class="vx-col sm:w-1/2 w-full">
                  <ValidationProvider v-slot="{ errors }" :name="$t('vdo-set-topic.no')" rules="required|numeric">
                    <vs-input v-model.number="form.vdoSetTopicInput.no" :label="$t('vdo-set-topic.no') + ' *'"
                              class="w-full"/>
                    <span v-show="errors[0]" class="text-danger text-sm">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>

              <div class="vx-row mb-2">
                <div class="vx-col sm:w-1/2 w-full">
                  <ValidationProvider v-slot="{ errors }" :name="$t('vdo-set-topic.name-local')" rules="required">
                    <vs-input v-model="form.vdoSetTopicInput.nameLocal" :label="$t('vdo-set-topic.name-local') + ' *'"
                              class="w-full"/>
                    <span v-show="errors[0]" class="text-danger text-sm">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
                <div class="vx-col sm:w-1/2 w-full">
                  <vs-input v-model="form.vdoSetTopicInput.nameEng" :label="$t('vdo-set-topic.name-eng')" class="w-full"/>
                </div>
              </div>

              <div class="vx-row mb-2">
                <div class="vx-col sm:w-1/2 w-full">
                  <ValidationProvider v-slot="{ errors }" :name="$t('vdo-set-topic.description-local')" rules="required">
                    <label class="text-sm">{{ $t('vdo-set-topic.description-local') + ' *' }}</label>
                    <vs-textarea v-model="form.vdoSetTopicInput.descriptionLocal" rows="2" class="mb-0"/>
                    <span v-show="errors[0]" class="text-danger text-sm">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
                <div class="vx-col sm:w-1/2 w-full">
                  <label class="text-sm">{{ $t('vdo-set-topic.description-eng') }}</label>
                  <vs-textarea v-model="form.vdoSetTopicInput.descriptionEng" rows="2"/>
                </div>
              </div>

              <div class="vx-row mb-2">
                <div class="vx-col w-full">
                  <label class="text-sm">{{ $t('vdo-set-topic.remark') }}</label>
                  <vs-textarea v-model="form.vdoSetTopicInput.remark" rows="2"/>
                </div>
              </div>

              <div class="vx-row mb-2">
                <div class="vx-col w-full">
                  <vs-checkbox v-model="form.vdoSetTopicInput.isEnabled" class="inline-flex">
                    {{ $t('vdo-set-topic.is-enabled') }}
                  </vs-checkbox>
                </div>
              </div>

              <!----------------------------------------- Actions Button ---------------------------------------------------->
              <div class="vx-row pt-2">
                <div class="vx-col sm:w-1/2 w-full">
                  <vs-button :disabled="local.isProcessing || invalid || isUploading"
                             @click.prevent="onSaveClick">
                    {{ $t('general.save') }}
                  </vs-button>
                  <vs-button v-if="showDeleteButton"
                             :disabled="local.isProcessing || isUploading"
                             class="ml-2" color="danger"
                             @click.prevent="onDeleteClick">
                    {{ $t('general.delete') }}
                  </vs-button>
                </div>
                <!--                  <div class="vx-col sm:w-1/2 w-full flex justify-end">
                                    <vs-button v-if="showDeleteButton"
                                               :disabled="local.isProcessing ||  invalid || isUploading"
                                               color="primary"
                                               @click.prevent="onUploadLearnTimeClick">
                                      <feather-icon icon="UploadIcon" svgClasses="h-4 w-4"></feather-icon>
                                      {{ $t('vdo-set-topic.upload-learn-time') }}
                                    </vs-button>
                                  </div>-->
              </div>
            </vs-tab>
            <!---------------------- UPLOAD VIDEO ------------------------>
            <vs-tab :label="$t('vdo-set-topic.video-topic')" :disabled="!showFileUpload">
              <div class="vx-row mb-2">
                <div class="vx-col w-full">
                  <borrowed-video-info :sharing="sharing"/>
                </div>
              </div>
              <div class="vx-row mb-2">
                <div class="vx-col sm:w-1/3 w-full">
                  <vs-button class="w-full"
                             type="border"
                             color="primary"
                             :disabled="local.isProcessing || isUploading || !currentVdoSetTopic"
                             @click.prevent="showBorrowDialog = true">
                    <feather-icon icon="LinkIcon" svgClasses="h-4 w-4"></feather-icon>
                    {{ $t('video-reuse.use-existing-video') }}
                  </vs-button>
                </div>
              </div>
              <div class="vx-row mb-2">
                <div class="vx-col w-full">
                  <bunny-upload-panel :topic="vdoSetTopicEdit.selectedVdoSetTopic"
                                      :is-uploading="isUploading"
                                      :percent="bunny.percent"
                                      :disabled="isBunnyProcessing"
                                      :show-download="showDownload"
                                      @file-selected="onBunnyFileSelected"
                                      @rejected-file="onBunnyFileRejected"
                                      @cancel="onBunnyCancel"
                                      @refresh="onBunnyRefresh"
                                      @delete="onDeleteVideoClick"
                                      @download="onBunnyDownload"/>
                </div>
              </div>
              <div class="vx-row mb-2">
                <div class="vx-col w-full" >
                  <!-- vdoStreamPath is a Wowza playback path. It is NOT cleared when a
                       topic moves to Bunny (Finding 8): clearing it would break playback
                       for the Wowza topics that still rely on it, with nothing to put in
                       its place. That means an existing Wowza topic whose university
                       later flips to Bunny can carry both a (stale) vdoStreamPath and a
                       (current) bunnyVideoGuid at once -- so Download and Check-corrupt,
                       which build a Wowza URL out of vdoStreamPath, are gated on
                       vdoStreamPath AND !isBunnyTopic rather than vdoStreamPath alone, or
                       they would render and act on the old Wowza video while the Bunny
                       badge beside them describes the new one.
                       Preview is no longer in that group (2026-08-18): it asks
                       canPreview(), which resolves the server-signed playbackUrl of
                       whichever provider actually holds the video, so it works for both
                       and cannot disagree with the player it opens. The enclosing
                       template below is what is gated on "has any video, from either
                       provider" -- the Delete Video button inside it is Wowza-only for
                       its own reasons, which it documents where it sits. -->
                  <template v-if="currentVdoSetTopic
                         && (currentVdoSetTopic.vdoStreamPath || currentVdoSetTopic.bunnyVideoGuid)
                         && !showPreviewVideo">
                    <div class="vx-row mb-2">
                      <div class="vx-col sm:w-1/4 w-full" v-if="canPreview(currentVdoSetTopic)">
                        <vs-button :disabled="local.isProcessing || isUploading"
                                   class="w-full"
                                   color="success"
                                   @click.prevent="onTriggerPreviewClick(true)">
                          <feather-icon icon="PlayIcon" svgClasses="h-4 w-4"></feather-icon>
                          {{ $t('vdo-set-topic.preview') }}
                        </vs-button>
                      </div>
                      <div class="vx-col sm:w-1/4 w-full"
                           v-if="showDownload && currentVdoSetTopic.vdoStreamPath && !isBunnyTopic">
                        <vs-button :disabled="local.isProcessing || isUploading"
                                   class="w-full"
                                   color="primary"
                                   @click.prevent="onDownloadVideoClick(currentVdoSetTopic)">
                          <feather-icon icon="DownloadIcon" svgClasses="h-4 w-4"></feather-icon>
                          {{ $t('general.download') }}
                        </vs-button>
                      </div>
                      <!-- Wowza-only now: the Bunny path's Delete lives in BunnyUploadPanel,
                           which gates it on stage. Without this condition a Bunny topic would
                           render two Delete buttons and this one would be reachable
                           mid-transcode. -->
                      <div class="vx-col sm:w-1/4 w-full" v-if="currentVdoSetTopic.vdoStreamPath && !isBunnyTopic">
                        <vs-button :disabled="local.isProcessing || isUploading"
                                   class="w-full"
                                   color="danger"
                                   @click.prevent="onDeleteVideoClick">
                          <feather-icon icon="TrashIcon" svgClasses="h-4 w-4"></feather-icon>
                          {{ $t('vdo-set-topic.delete-only-video') }}
                        </vs-button>
                      </div>
                      <div class="vx-col sm:w-1/4 w-full">
                        <vs-button v-if="showDeleteButton"
                                   :disabled="local.isProcessing ||  invalid || isUploading"
                                   color="primary"
                                   @click.prevent="onUploadLearnTimeClick">
                          <feather-icon icon="UploadIcon" svgClasses="h-4 w-4"></feather-icon>
                          {{ $t('vdo-set-topic.upload-learn-time') }}
                        </vs-button>
                      </div>
                    </div>
                    <div class="vx-row" v-if="currentVdoSetTopic.vdoStreamPath && !isBunnyTopic">
                      <div class="vx-col sm:w-1/4 w-full">
                        <vs-button :disabled="local.isProcessing || isUploading"
                                   class="w-full"
                                   color="warning"
                                   @click.prevent="onCheckVideoCorrupt(currentVdoSetTopic.vdoStreamPath)">
                          <feather-icon icon="CheckIcon" svgClasses="h-4 w-4"></feather-icon>
                          {{ $t('vdo-set-topic.check-vdo-corrupt') }}
                        </vs-button>
                      </div>
                    </div>
                  </template>
                </div>
              </div>

            </vs-tab>
            <!---------------------- UPLOAD THUMBNAIL ------------------------>
            <vs-tab :label="$t('vdo-set-topic.thumbnail')" :disabled="!showFileUpload">
              <div class="vx-row mb-2">
                <div class="vx-col sm:w-1/2 w-full text-center">
                  <img ref="have-avatar"
                       width="200"
                       height="112"
                       :src="thumbnailUrl"
                       style="display: block; margin-left: auto; margin-right: auto;"
                  />
                </div>
                <div class="vx-col sm:w-1/2 w-full">
                  <input type="file" class="hidden" ref="update_picture_input"
                         @change="uploadPictureChange"
                         accept="image/*">

                  <vs-button color="primary" icon-pack="feather" icon="icon-upload"
                             class="w-full"
                             @click="$refs.update_picture_input.click()">
                    {{ $t('course-outline.upload-course-picture') }}
                  </vs-button>

                  <p class="mt-2">{{ $t('vdo-set-topic.thumbnail-size') }}</p>
                  <p>{{ $t('vdo-set-topic.thumbnail-type') }}</p>
                </div>
              </div>
            </vs-tab>
          </vs-tabs>


        </template>

      </div>
    </div>

    <borrow-video-dialog :active.sync="showBorrowDialog"
                         :topic="currentVdoSetTopic"
                         @selected="onBorrowSelected"/>
  </ValidationObserver>
</template>

<script>
// import axios from "axios";
import axios from "@/setup/axios-setup";
// import * as crypto from "crypto";
// import CryptoJS from 'crypto-js';
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {setEmptyVdoSetTopicForm, state} from '../data';
import {secondToHHMMSSFormat} from "@/utils/data";
import {notificationType, showLocalNotification, showStateNotification} from "@/utils/notification";
// import {confirmDelete, confirmSave} from "@/utils/dialogs";
import {confirmDelete, successDialog, errorDialog, warningDialog} from "@/utils/dialogs";
import {config, getPhotoUrl} from "@/utils/upload";
// import vsUploadMod from '@/components/vuesax-mod/components/vsUpload/vsUpload'
import {getECode, getRStage, getToken} from "@/utils/auth-data";
import PreviewVideoCard from "@/views/pages/VdoSet/SubComponents/PreviewVideoCard";
import UploadLearnTimeCard from "@/views/pages/VdoSet/SubComponents/UploadLearnTimeCard";

import VdoSetTopic from '@/store/modules/vdo-set/vdo-set-topic/index';
import Upload from "@/store/modules/upload";
import {createHelpers} from 'vuex-map-fields';
import {createNamespacedHelpers} from "vuex";
import {isTerminal} from '@/utils/bunny-status';
import {shouldAutoPoll} from '@/utils/bunny-upload-stage';
import {resolveVideoSource} from '@/utils/video-source';
import {startBunnyUpload} from '@/utils/bunny-upload';
import {buildBunnyTitle} from '@/utils/bunny-title';
import {withVdoLength} from '@/utils/vdo-length';
import BunnyUploadPanel from './BunnyUploadPanel.vue';
import BorrowVideoDialog from './BorrowVideoDialog.vue';
import BorrowedVideoInfo from './BorrowedVideoInfo.vue';
import {isVideoDownloadEnabled} from '@/utils/video-provider';
import BunnyUpload from '@/store/modules/vdo-set/bunny-upload';

// How often startBunnyPolling() checks Bunny's status (Finding 10).
const BUNNY_POLL_INTERVAL_MS = 10000;
// Upper bound on how long the poll loop runs unattended before giving up and
// asking the admin to hit Refresh manually. 2 hours of 10s ticks. This is not
// meant to bound a slow encode -- Bunny transcoding a multi-hour lecture (the
// same files the 24-hour TUS signature exists for) can routinely take longer
// than 30 minutes, and hitting the cap mid-encode would make "still
// processing" a normal outcome instead of the anomaly it's meant to catch
// (Finding 6). What this actually guards against is a tab left open forever:
// a topic that never reaches a terminal status (a stuck Bunny job, a
// permanently missed webhook combined with the admin never revisiting the
// screen) polls -- one GraphQL mutation plus one Bunny REST call per tick --
// for as long as the tab stays open.
const BUNNY_POLL_MAX_TICKS = (2 * 60 * 60 * 1000) / BUNNY_POLL_INTERVAL_MS;

// Consecutive failed refreshes before the loop gives up. A refresh returns
// null on any error, and the action commits setError each time, which the
// bunnyUploadError watcher turns into a toast -- so a loop that keeps failing
// is not merely useless, it is one toast every ten seconds for as long as it
// runs. Three tolerates a blip (a dropped connection, a backend restart)
// without tolerating a topic whose video no longer exists.
const BUNNY_POLL_MAX_FAILURES = 3;

if (!state.store.VdoSetTopic) state.registerModule(`VdoSetTopic`, VdoSetTopic);
if (!state.store.Upload) state.registerModule(`Upload`, Upload);
if (!state.store.BunnyUpload) state.registerModule(`BunnyUpload`, BunnyUpload);

const {
  mapActions: mapVdoSetTopicActions,
} = createNamespacedHelpers(`VdoSetTopic`);
const {
  mapActions: mapUploadActions,
} = createNamespacedHelpers(`Upload`);
const {
  mapActions: mapBunnyUploadActions,
} = createNamespacedHelpers(`BunnyUpload`);

const {
  mapFields: mapVdoSetTopicFields,
} = createHelpers({
  getterType: `VdoSetTopic/getField`,
  mutationType: `VdoSetTopic/updateField`,
});
const {
  mapFields: mapBunnyUploadFields,
} = createHelpers({
  getterType: `BunnyUpload/getField`,
  mutationType: `BunnyUpload/updateField`,
});


export default {
  components: {
    // vsUploadMod,
    PreviewVideoCard,
    VuePerfectScrollbar,
    UploadLearnTimeCard,
    BunnyUploadPanel,
    BorrowVideoDialog,
    BorrowedVideoInfo,
  },
  data() {
    return {
      settings: {      // perfectScrollbar settings
        maxScrollbarLength: 30,
        wheelSpeed: 1,
        wheelPropagation: false,
        swipeEasing: true
      },
      store: state.store,
      local: state.local,

      vdoSetEdit: state.local.vdoSetEdit,
      vdoSetInfo: state.local.vdoSetInfo,
      vdoSetUnitEdit: state.local.vdoSetUnitEdit,
      vdoSetTopicEdit: state.local.vdoSetTopicEdit,

      form: state.local.vdoSetTopicEdit.form,

      uploadAcceptVdoType: config.fileExtension.video,
      uploadVdoUrl: config.url.vdoOnDemandApi,
      uploadPayload: {
        fileName: '',
        vdoSetTopicId: '',
        subjectCode: '',
        vdoLength: 0,
        r_stage: getRStage,
        e_code: getECode,
        authorization: getToken,
        app: "admin",
        appName: "xstudi",
        checksum: null,
        teacherCode: '',
      },
      bunny: {
        percent: 0,
        upload: null,
        pollTimer: null,
        // Bumped on every onBunnyFileSelected entry and on onBunnyCancel. The
        // post-await path in onBunnyFileSelected compares against this to
        // detect a cancel that landed while the ticket request was still
        // in flight (Finding 3).
        generation: 0,
        // Ticks elapsed in the current poll loop, reset each time
        // startBunnyPolling() is (re)started. Compared against
        // BUNNY_POLL_MAX_TICKS (Finding 10).
        pollTicks: 0,
        // Consecutive ticks whose refresh came back empty, reset by any tick
        // that succeeds. Compared against BUNNY_POLL_MAX_FAILURES.
        pollFailures: 0,
        // Built once per upload by onBunnyFileSelected and reused verbatim by
        // refreshTicket. Recomputing it there would rename a video partway
        // through its own transfer, because the timestamp would move.
        title: '',
      },

      showPreviewVideo: false,
      loading: false,

      showBorrowDialog: false,
      // getVdoSetTopicSharing result for the topic on screen. Loaded on demand
      // rather than folded into getVdoSetTopic because videoSharedWith costs an
      // extra server query per topic.
      sharing: null,

      //for upload learn time
      showUploadLearnTime: false,

      //thumbnail
      fileUpload: null,
      allowWidth: 200,
      allowHeight: 112,
      fileWidth: 0,
      fileHeight: 0,

      selectedTab: 0,
    }
  },
  computed: {
    ...mapVdoSetTopicFields({
      vdoSetTopicProcessing: 'processing',
      vdoSetTopicSuccess: 'success',
      vdoSetTopicError: 'error'
    }),
    ...mapBunnyUploadFields({
      bunnyUploadProcessing: 'processing',
      bunnyUploadSuccess: 'success',
      bunnyUploadError: 'error'
    }),
    isUploading() {
      return this.local.isUploading
    },
    // BunnyUpload's own `processing` field was mapped nowhere, so every
    // setProcessing commit in bunny-upload/actions was inert and the panel's
    // :disabled prop only ever reflected VdoSetTopic's watcher -- two clicks
    // on e.g. Download fired two backend queries and two Bunny API calls.
    isBunnyProcessing() {
      return this.local.isProcessing || this.bunnyUploadProcessing;
    },
    vdoSetTopics() {
      return this.local.vdoSetUnitEdit.selectedVdoSetUnit ? this.local.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics : [];
    },
    vdoSetTopicFormHeader() {
      if (this.vdoSetTopicEdit.selectedVdoSetTopic) {
        return this.$t('general.edit') + this.$t('vdo-set.vdo-set-topic');
      } else {
        return this.$t('general.add') + this.$t('vdo-set.vdo-set-topic');
      }
    },
    showWarningNoTopics() {
      if (this.local.vdoSetUnitEdit.selectedVdoSetUnit
        && this.local.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics
        && this.local.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics.length > 0) {
        return false;
      }
      return true;
    },
    showFileUpload() {
      if (this.vdoSetTopicEdit.selectedVdoSetTopic && this.vdoSetTopicEdit.selectedVdoSetTopic.id) {
        return true;
      } else {
        return false;
      }
    },
    showDeleteButton() {
      if (this.vdoSetTopicEdit.selectedVdoSetTopic && this.vdoSetTopicEdit.selectedVdoSetTopic.id) {
        return true;
      } else {
        return false;
      }
    },
    currentVdoSetTopic() {
      if (this.vdoSetTopicEdit.selectedVdoSetTopic) {
        return this.vdoSetTopicEdit.selectedVdoSetTopic
      }
      return null;
    },
    thumbnailUrl() {
      if (this.fileUpload) {
        return URL.createObjectURL(this.fileUpload)
      }
      else if (!this.fileUpload && this.form.vdoSetTopicInput && this.form.vdoSetTopicInput.thumbnail) {
        return getPhotoUrl(this.form.vdoSetTopicInput.thumbnail);
      }
      else {
        return config.url.noPhotoImage;
      }
    },
    // Finding 8: videoProvider, not vdoStreamPath, is what actually marks a
    // topic as Bunny's -- it is set once by createBunnyUpload and never
    // cleared, so it stays true even after a Bunny video is deleted. An
    // existing Wowza topic whose university then flips to Bunny keeps its
    // old vdoStreamPath (deliberately -- Phase 2 owns the Bunny playback
    // reference, and clearing it now would break Wowza playback with
    // nothing to replace it), so vdoStreamPath alone is no longer proof a
    // topic's Preview/Download/Check-corrupt controls should render.
    isBunnyTopic() {
      const topic = this.vdoSetTopicEdit.selectedVdoSetTopic;
      return !!(topic && topic.videoProvider === 'bunny');
    },
    // Read through a computed rather than called from each v-if so the list
    // icon, the Wowza button and BunnyUploadPanel cannot end up disagreeing.
    showDownload() {
      return isVideoDownloadEnabled();
    },
  },
  watch: {
    vdoSetTopicProcessing(data) {
      this.local.isProcessing = data;
    },
    vdoSetTopicSuccess(data) {
      showStateNotification(this.$vs, data);
    },
    vdoSetTopicError(data) {
      showStateNotification(this.$vs, data);
    },
    bunnyUploadSuccess(data) {
      showStateNotification(this.$vs, data);
    },
    bunnyUploadError(data) {
      showStateNotification(this.$vs, data);
    },
    loading(data) {
      this.local.isProcessing = data;
    },
    showFileUpload(value){
      if(!value){
        this.setTabDefaultPosition()
      }
    },
    currentVdoSetTopic: {
      immediate: true,
      handler() {
        this.loadSharing();
      },
    },
  },
  methods: {
    //---------------------------------------- State Function ------------------------------------------------------//
    ...mapVdoSetTopicActions({
      getVdoSetTopic: 'getVdoSetTopic',
      addVdoSetTopic: 'addVdoSetTopic',
      updateVdoSetTopic: 'updateVdoSetTopic',
      updateOnlyVdoSetTopic: 'updateOnlyVdoSetTopic',
      deleteVdoSetTopic: 'deleteVdoSetTopic',
      deleteOnlyVideo: 'deleteOnlyVideo',
      borrowVdoSetTopicVideo: 'borrowVdoSetTopicVideo',
      getVdoSetTopicSharing: 'getVdoSetTopicSharing',
    }),
    ...mapUploadActions({
      uploadAvatar: 'uploadAvatar',
    }),
    ...mapBunnyUploadActions(['createBunnyUpload', 'refreshBunnyStatus', 'cancelBunnyUpload', 'deleteBunnyVideo', 'fetchBunnyDownloadUrl']),
    //---------------------------------------- Event Function ------------------------------------------------------//
    onAddClick() {
      if (!this.local.isUploading) {
        this.setTabDefaultPosition();
        setEmptyVdoSetTopicForm();
        this.clearVideoUploadPayload();
        this.showPreviewVideo = false;
        this.showUploadLearnTime = false;

        // Nothing is selected any more. Same call as every other site so
        // there is one rule to remember; with no topic it stops the timer and
        // starts nothing.
        this.maybeStartPollingFor(null);
      }
    },
    onEditClick(selectedData) {
      if (!this.local.isUploading) {
        this.setTabDefaultPosition();
        this.showPreviewVideo = false;
        this.showUploadLearnTime = false;

        this.vdoSetTopicEdit.selectedVdoSetTopic = selectedData;

        this.form.vdoSetTopicInput.no = selectedData.no;
        this.form.vdoSetTopicInput.nameLocal = selectedData.nameLocal;
        this.form.vdoSetTopicInput.nameEng = selectedData.nameEng;
        this.form.vdoSetTopicInput.descriptionLocal = selectedData.descriptionLocal;
        this.form.vdoSetTopicInput.descriptionEng = selectedData.descriptionEng;
        this.form.vdoSetTopicInput.vdoLength = selectedData.vdoLength;
        this.form.vdoSetTopicInput.remark = selectedData.remark;
        this.form.vdoSetTopicInput.isEnabled = selectedData.isEnabled;
        this.form.vdoSetTopicInput.thumbnail = selectedData.thumbnail;

        //set id for api upload
        this.uploadPayload.vdoSetTopicId = selectedData.id;
        this.uploadPayload.subjectCode = this.vdoSetEdit.courseOutline.subject.code;
        this.uploadPayload.fileName = '';
        this.uploadPayload.fileNameUpload = '';
        this.uploadPayload.vdoLength = 0;
        this.uploadPayload.checksum = null;
        this.uploadPayload.teacherCode = this.vdoSetEdit.courseOutline.teacher.code;

        // Deliberately not awaited: selecting a topic must feel instant, and
        // the refresh writes onto the topic object through applyBunnyTopic
        // when it lands.
        this.maybeStartPollingFor(selectedData);
      }
    },
    async onSaveClick() {
      let payload = this.prepareDataBeforeSave();
      // console.log('onSaveClick', payload);
      // confirmSave(this.$vs, this.saveVdoSetTopic, payload);

      await this.saveVdoSetTopic(payload);
    },
    async onDeleteClick() {
      let payload = {
        id: this.vdoSetTopicEdit.selectedVdoSetTopic.id
      }
      confirmDelete(this.$vs, this.removeVdoSetTopic, payload);
    },
    async onDeleteVideoClick() {
      let payload = {
        id: this.vdoSetTopicEdit.selectedVdoSetTopic.id
      }
      // Deleting a video is irreversible and drops this topic's vdoLength to
      // zero, which moves the unit total every enrolled student's learn
      // percent is measured against. The generic "are you sure" text does not
      // convey that, so both branches below get an explicit warning. The
      // backend refuses outright when a current-period student has already
      // learned the topic (hasCurrentPeriodStudentTopicLearn), but the admin
      // should know the stakes before clicking, not only after being blocked.
      const confirmMessage = this.$t('vdo-set-topic.confirm-delete-video-message');

      const sharedMessage = this.sharedDeleteMessage();
      const finalMessage = sharedMessage ?
        `${confirmMessage || ''}\n${sharedMessage}`.trim() :
        confirmMessage;

      // A Bunny topic must go through deleteBunnyVideo (Finding 6): the
      // Wowza deleteOnlyVideo mutation below only clears vdoLength and
      // vdoStreamPath and never touches bunnyVideoGuid, which would leave
      // the video sitting in Bunny forever while the UI reports success.
      const topic = this.vdoSetTopicEdit.selectedVdoSetTopic;
      if (topic && topic.bunnyVideoGuid) {
        confirmDelete(this.$vs, this.removeBunnyVideo, payload, finalMessage);
        return;
      }
      confirmDelete(this.$vs, this.removeVdoSetTopicVideo, payload, finalMessage);
    },
    onPreviewVideoClick(selectedData){
      this.onEditClick(selectedData);
      this.onTriggerPreviewClick(true);
    },
    onTriggerPreviewClick(trigger) {
      this.showPreviewVideo = trigger;
    },
    // Whether this topic has something playable right now, asked of the same
    // function PreviewVideoCard builds its player source from.
    //
    // These controls used to be gated on vdoStreamPath, which is a Wowza
    // column that is deliberately never written for a Bunny topic -- so the
    // admin who had just uploaded a Bunny video had no way at all to watch it
    // back and confirm it, and a stale status reading was indistinguishable
    // from a broken video. Routing the button and the player through one
    // function means they cannot disagree: if the control renders, a source
    // exists, so there is no "click Preview, get a spinner forever" state.
    //
    // resolveVideoSource throws when playbackStatus was not selected by the
    // query that loaded the topic. That is the intended backstop behind
    // scripts/check-video-fields.js, and every query feeding this screen
    // selects it -- a throw here would be a real regression and should stay
    // loud rather than quietly hiding the control.
    canPreview(topic) {
      return !!resolveVideoSource(topic);
    },
    // Single entry point for "download this topic's video", so the list panel
    // and the edit panel cannot drift onto different providers. Branches on
    // bunnyVideoGuid rather than videoProvider for the same reason
    // onDeleteVideoClick does: videoProvider stays 'bunny' after a Bunny video
    // is deleted, so it would route a topic that has no video at all down the
    // Bunny path.
    onDownloadClick(vdoSetTopic) {
      if (!vdoSetTopic) return;
      if (vdoSetTopic.bunnyVideoGuid) {
        this.onBunnyDownload(vdoSetTopic);
        return;
      }
      this.onDownloadVideoClick(vdoSetTopic);
    },
    onDownloadVideoClick(vdoSetTopic) {
      this.loading = true;
      const streamPath = vdoSetTopic.vdoStreamPath;
      const url = `${config.url.downloadVideo}/${getECode}/${getRStage}/${streamPath}`;

      axios.get(url, {responseType: 'blob'})
        .then(response => {
          let fileNameSplit = streamPath.split('/');
          fileNameSplit = fileNameSplit[fileNameSplit.length - 1].split('.');
          const fileExtension = fileNameSplit[fileNameSplit.length - 1];
          const subjectCode = this.vdoSetEdit.courseOutline.subjectCode;
          const unitSeq = this.vdoSetUnitEdit.selectedVdoSetUnit.unitSeq;
          const topicNo = vdoSetTopic.no
          const topicName = vdoSetTopic.nameLocal
          const fileName = `${subjectCode}_${unitSeq}_${topicNo}_${topicName}.${fileExtension}`;

          const blob = new Blob([response.data], {type: 'video/*'});
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(link.href);
        }).finally(() => {
        this.loading = false;
      });
    },
    // vdoSetTopic defaults to the selected topic so BunnyUploadPanel's
    // `$emit('download')` (no payload) keeps working; the list panel passes
    // its own row, which is not necessarily the selected one.
    async onBunnyDownload(vdoSetTopic) {
      const topic = vdoSetTopic || this.vdoSetTopicEdit.selectedVdoSetTopic;
      if (!topic || !topic.id) return;

      // Opened synchronously, before the await: Safari and iOS only honour
      // window.open inside the user gesture, so opening it after the round
      // trip would be popup-blocked and the button would silently do nothing.
      const tab = window.open('', '_blank');
      if (tab) tab.opener = null;

      // Deliberately NOT fetched through @/setup/axios-setup like the Wowza
      // download above: that instance attaches the StudX JWT to every
      // request with no host check, which would hand a user token to
      // bunny.net.
      //
      // The cost is the filename -- the file arrives as play_720p.mp4
      // rather than the subject/unit/topic name the Wowza path builds,
      // because the download attribute is ignored cross-origin.
      const url = await this.fetchBunnyDownloadUrl(topic.id);
      if (!url) {
        if (tab) tab.close();
        return;
      }

      if (tab) {
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
    },
    // The panel already filters by extension for the click-to-browse path
    // (vue2-dropzone's own acceptedFiles); this covers the drag-and-drop
    // path, which vue2-dropzone does not filter at all.
    onBunnyFileRejected(file) {
      showLocalNotification(this.$vs, this.$t('general.error'),
        this.$t('upload.upload-vdo-fail', {fileName: file && file.name}),
        notificationType.ERROR);
    },
    async onBunnyFileSelected(file) {
      if (!file) return;

      // Covers the whole function: a second file reaching us while a ticket
      // request is still in flight must not start a second upload.
      if (this.local.isUploading) return;

      const topic = this.vdoSetTopicEdit.selectedVdoSetTopic;
      if (!topic || !topic.id) {
        showLocalNotification(this.$vs, this.$t('general.error'),
          this.$t('upload.upload-vdo-fail', {fileName: file.name}),
          notificationType.ERROR);
        return;
      }

      // Set synchronously, before the first await, so the guard above and the
      // template's `:disabled="isUploading"` both cover the ticket-request
      // window too.
      this.local.isUploading = true;
      this.bunny.percent = 0;
      // Captured now; compared again after the ticket await resolves so a
      // cancel that lands during that wait (Finding 3) is not ignored. Also
      // bumped here (not only on cancel) so a later legitimate upload is
      // never blocked by a generation left stale by an earlier cancel.
      const generation = ++this.bunny.generation;

      // Seed vdoLength from the browser so the UI is usable immediately;
      // syncFromBunny overwrites it with Bunny's authoritative value. A
      // false return means the browser could not decode the file (e.g. one
      // that slipped past extension filtering) -- treat that as a failure
      // rather than starting a Bunny upload for a file that has no usable
      // metadata.
      const decoded = await this.setVdoInfo(file, this.uploadPayload);
      if (!decoded) {
        this.local.isUploading = false;
        showLocalNotification(this.$vs, this.$t('general.error'),
          this.$t('upload.upload-vdo-fail', {fileName: file.name}),
          notificationType.ERROR);
        return;
      }

      // Captured once, here. Both the initial ticket request and the
      // refreshTicket that fires when a 24-hour TUS signature expires
      // mid-transfer send this same string -- recomputing it would rename
      // the video partway through its own upload.
      this.bunny.title = buildBunnyTitle({
        subjectCode: this.vdoSetEdit.courseOutline.subject.code,
        unitSeq: this.vdoSetUnitEdit.selectedVdoSetUnit.unitSeq,
        topicNo: topic.no,
        topicName: topic.nameLocal,
        topicId: topic.id,
        uploadedAt: new Date(),
      });

      const ticket = await this.createBunnyUpload({
        vdoSetTopicId: topic.id,
        title: this.bunny.title,
      });

      // The user may have clicked cancel while the ticket request was still
      // awaiting -- onBunnyCancel already reset isUploading/percent and
      // bumped the generation. Starting the upload now would ignore that.
      if (generation !== this.bunny.generation) return;

      if (!ticket) {
        this.local.isUploading = false;
        return;
      }

      this.bunny.upload = startBunnyUpload({
        file,
        ticket,
        // The same string createBunnyUpload was given above. Bunny's TUS
        // metadata title lands after the one the mutation set on the video
        // object and overwrites it, so anything else here -- the file's own
        // name, most of all -- silently wins over buildBunnyTitle.
        title: this.bunny.title,
        // The signature lives 24 hours, and a large file on campus internet can
        // outlive it. Task 8's wrapper handles that only if it is given a way to
        // ask for a new one -- the same mutation, same guid, so the upload
        // resumes from its offset rather than starting a second video.
        refreshTicket: () => this.createBunnyUpload({
          vdoSetTopicId: topic.id,
          title: this.bunny.title,
        }),
        onProgress: (percent) => { this.bunny.percent = percent; },
        onSuccess: async () => {
          this.local.isUploading = false;
          await this.updateVdoLength();
          showLocalNotification(this.$vs, this.$t('general.success'),
            this.$t('upload.upload-vdo-success', {fileName: file.name}),
            notificationType.SUCCESS);
          // Track by id, not object reference. updateVdoLength() above
          // already replaced both the selection and the list entry with a
          // brand-new object from the server response -- `topic` itself is
          // orphaned by now, but `topic.id` is still the right key. Each
          // poll tick resolves whichever live object(s) currently carry
          // that id (Finding 4, round 2).
          this.startBunnyPolling(topic.id, ticket.videoGuid);
        },
        onError: (err) => {
          this.local.isUploading = false;
          showLocalNotification(this.$vs, this.$t('general.error'),
            this.$t('upload.upload-vdo-fail', {fileName: file.name}) + ' ' + err.message,
            notificationType.ERROR);
        },
      });
    },

    async onBunnyCancel() {
      // Invalidates a ticket request from onBunnyFileSelected that is still
      // awaiting createBunnyUpload -- when it resolves it will see its
      // captured generation is stale and return before starting the upload.
      this.bunny.generation++;

      // PROCESSING is the only cancellable stage that has a live poll timer
      // (UPLOADING and ABANDONED offer Cancel too, but neither is ever
      // polling, so this is a no-op there). The mutation below clears
      // bunnyVideoGuid, after which refreshBunnyStatus throws for this topic
      // on every subsequent tick -- one error toast every ten seconds until
      // the tab is reloaded. Stop first, so there is no tick to throw.
      this.stopBunnyPolling();

      if (this.bunny.upload) {
        this.bunny.upload.abort();
        this.bunny.upload = null;
      }
      this.local.isUploading = false;
      this.bunny.percent = 0;

      const topic = this.vdoSetTopicEdit.selectedVdoSetTopic;
      if (topic && topic.id) {
        const updated = await this.cancelBunnyUpload(topic.id);
        if (updated) {
          this.applyBunnyTopic(topic, updated);
          return;
        }
        // The cancel failed, so the video is still encoding -- but the timer
        // was stopped above on the assumption it would succeed. Put it back,
        // or the status silently stops tracking a video that is still moving
        // until the admin happens to reselect the topic.
        this.maybeStartPollingFor(topic);
      }
    },

    async onBunnyRefresh() {
      const topic = this.vdoSetTopicEdit.selectedVdoSetTopic;
      if (!topic || !topic.id) return;
      const updated = await this.refreshBunnyStatus(topic.id);
      if (updated) this.applyBunnyTopic(topic, updated);
    },

    // Writes a refreshed topic from the server onto the object the screen is
    // rendering. Every field the mutation returns and the screen reads has to
    // be here: playbackUrl and playbackStatus were being dropped, so a poll
    // tick that saw the encode finish updated the badge but left
    // playbackStatus at its stale 'ENCODING' value -- and canPreview(), which
    // reads exactly that, kept the Preview control hidden on a video that was
    // ready to watch, until the admin navigated away and back.
    //
    // playbackUrl is signed and expires (~8h -- see resolvePlayback in the
    // backend), and every refresh replaces it with a freshly signed one. Note
    // what that does NOT cover: polling stops once the status is terminal, so
    // a panel left open all day keeps the last URL it was given and Preview
    // can eventually hand the player an expired token. Reselecting the topic
    // re-queries and fixes it. Better than before, when the field was never
    // refreshed at all, but not a solution to token lifetime.
    //
    // Writes are last-one-wins and deliberately unguarded. Two refreshes for
    // the same topic can resolve out of order, in which case the older
    // snapshot lands last and the badge can regress for up to one tick before
    // the next tick corrects it. The alternative -- sequencing every write --
    // costs more than the wrinkle.
    applyBunnyTopic(topic, updated) {
      if (!topic) return;
      this.$set(topic, 'videoProvider', updated.videoProvider);
      this.$set(topic, 'bunnyVideoGuid', updated.bunnyVideoGuid);
      this.$set(topic, 'bunnyStatus', updated.bunnyStatus);
      this.$set(topic, 'bunnyStatusAt', updated.bunnyStatusAt);
      this.$set(topic, 'vdoLength', updated.vdoLength);
      this.$set(topic, 'playbackUrl', updated.playbackUrl);
      this.$set(topic, 'playbackStatus', updated.playbackStatus);
    },

    // Start (or stop) the status poll for whichever topic is now on screen.
    //
    // Until this existed, startBunnyPolling had exactly one call site -- the
    // TUS onSuccess handler -- so the panel only ever learned a status from an
    // upload performed in that same page session. Reload the page, reopen a
    // topic mid-encode, and the stage sat frozen at whatever the last query
    // had returned. That is what made an unrelated save appear to change the
    // video's status: updateListAfterSave replaces the topic object wholesale,
    // so the save was simply the first thing to fetch fresh data.
    //
    // Safe to call with null (nothing selected): it stops the timer and starts
    // nothing.
    async maybeStartPollingFor(topic) {
      this.stopBunnyPolling();
      if (!shouldAutoPoll({isUploading: this.isUploading, topic, now: Date.now()})) return;

      // Once up front rather than waiting out the first ten-second tick. The
      // whole point is that the value on screen the moment a topic opens is
      // the real one.
      const updated = await this.refreshBunnyStatus(topic.id);
      if (updated) this.applyBunnyTopic(topic, updated);

      // Re-checked after the await: that refresh may have reported a terminal
      // status, and the admin may have selected something else while it was in
      // flight. Starting a loop for either would poll a thing that cannot
      // change, or a thing nobody is looking at.
      if (this.vdoSetTopicEdit.selectedVdoSetTopic !== topic) return;
      if (!shouldAutoPoll({isUploading: this.isUploading, topic, now: Date.now()})) return;

      this.startBunnyPolling(topic.id, topic.bunnyVideoGuid);
    },

    // `topicId` is the topic this loop belongs to, captured by value. A tick
    // must never re-read "whichever topic is selected now" to decide *what* to
    // poll, and it cannot hold an object reference either (Finding 4, round
    // 2): updateVdoLength() in onSuccess -- and updateListAfterSave on any
    // save -- replaces both the selection and the list entry with a brand-new
    // object, so anything captured earlier is already orphaned. Each tick
    // therefore resolves the *current* live object(s) for topicId fresh and
    // writes onto those.
    //
    // Which topic gets a loop changed on 2026-08-18. It used to be only the
    // one whose upload had just finished; it is now whichever topic is
    // selected, re-armed by maybeStartPollingFor on every selection and after
    // every save. The cost is that finishing an upload on topic A and then
    // switching to B leaves A's row un-refreshed until A is selected again.
    // That is display lag and nothing more: Bunny's webhook is what writes
    // bunnyStatus to the database and it keeps running either way, so no value
    // can end up wrong. What it buys is one rule instead of two -- the screen
    // tracks the topic you are looking at, while it is still working.
    startBunnyPolling(topicId, videoGuid) {
      this.stopBunnyPolling();
      if (!topicId || !videoGuid) return;
      this.bunny.pollTicks = 0;
      this.bunny.pollFailures = 0;
      this.bunny.pollTimer = setInterval(async () => {
        // Finding 10: stop after BUNNY_POLL_MAX_TICKS (~2 hours) rather
        // than polling forever when a topic never reaches a terminal status.
        // Told through the centralised notification helper, not a hand-rolled
        // $vs.notify, so it stays consistent with every other message on
        // this screen.
        this.bunny.pollTicks += 1;
        if (this.bunny.pollTicks > BUNNY_POLL_MAX_TICKS) {
          this.stopBunnyPolling();
          showLocalNotification(this.$vs, this.$t('general.warning'),
            this.$t('vdo-set-topic.bunny-poll-timeout'),
            notificationType.WARNING);
          return;
        }

        const updated = await this.refreshBunnyStatus(topicId);
        // A null means the refresh errored; the action has already reported
        // it. Retrying is right for a transient failure and wrong for a
        // permanent one -- a cancelled or deleted topic makes the backend
        // throw on every future tick, and each throw is another toast. Give
        // up after a few in a row, and let any success clear the count.
        //
        // Consecutive, deliberately: a backend that alternates failure and
        // success is still working, and the count resets each time it does.
        // The bound on that case is BUNNY_POLL_MAX_TICKS, not this.
        if (!updated) {
          this.bunny.pollFailures += 1;
          if (this.bunny.pollFailures >= BUNNY_POLL_MAX_FAILURES) this.stopBunnyPolling();
          return;
        }
        this.bunny.pollFailures = 0;

        const targets = [];
        const selected = this.vdoSetTopicEdit.selectedVdoSetTopic;
        if (selected && selected.id === topicId) targets.push(selected);

        const list = this.vdoSetUnitEdit.selectedVdoSetUnit
          ? this.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics
          : [];
        const listEntry = list.find(item => item.id === topicId);
        if (listEntry && !targets.includes(listEntry)) targets.push(listEntry);

        // Neither the selection nor the unit's topic list still holds this
        // id -- the admin has navigated away from the unit entirely. There
        // is nothing left to write status onto, so stop rather than spin.
        if (targets.length === 0) {
          this.stopBunnyPolling();
          return;
        }

        targets.forEach(target => this.applyBunnyTopic(target, updated));

        if (isTerminal(updated.bunnyStatus)) this.stopBunnyPolling();
      }, BUNNY_POLL_INTERVAL_MS);
    },

    stopBunnyPolling() {
      if (this.bunny.pollTimer) {
        clearInterval(this.bunny.pollTimer);
        this.bunny.pollTimer = null;
      }
    },
    // upload learn time function
    onUploadLearnTimeClick(){
      this.showUploadLearnTime = true;
    },
    closeUploadLearnTimeCard(){
      this.showUploadLearnTime = false
    },
    async uploadPictureChange(e) {
      if (e.target.files && e.target.files.length > 0) {
        let file = e.target.files[0];

        //500000 b = 500 kb
        if(file.size >= 500000){
          showLocalNotification(this.$vs
            , this.$t('general.warning')
            , this.$t('course-outline.file-to-large')
            , notificationType.WARNING)
          file = null;
          return;
        }

        let reader = new FileReader();
        reader.readAsDataURL(file);
        const _this = this
        reader.onload =  async function(evt) {
          let img = new Image();
          img.onload = async () => {
            //check image resolution
            if(img.width !== _this.allowWidth || img.height !== _this.allowHeight) {
              showLocalNotification(_this.$vs
                , _this.$t('general.warning')
                , _this.$t('general.wrong-image-resolution')
                , notificationType.WARNING)
              file = null;
              return;
            }

            _this.fileUpload = file
            //do upload
            let uploadPayload = {
              file: file,
              operationName: config.operationName.coverPhoto,
            }
            let resultUpload = await _this.uploadAvatar(uploadPayload);

            if (resultUpload) {
              await _this.updateAfterUploadThumbnail(resultUpload.file.uuid);
            }

          }
          img.src = evt.target.result;
        }

      } else {
        this.fileUpload = null;
        this.form.vdoSetTopicInput.thumbnail = '';
      }
    },
    async onCheckVideoCorrupt(vdoStreamPath){
      try {
        this.loading = true;

        const {data} = await axios.post(config.url.checkVideoCorrupt,
          {
            filePath: vdoStreamPath
          }, {
            headers: {
              'Content-Type': 'application/json',
              'authorization': getToken,
            }
          }
        )

        if (data.corrupt === null) {
          await warningDialog(this.$vs, data.message);
        } else if (data.corrupt === false) {
          await successDialog(this.$vs, data.message);
        } else if (data.corrupt === true) {
          await errorDialog(this.$vs,  data.message);
        }

      } catch (e) {
        showLocalNotification(this.$vs, this.$t('general.error'), e.message, notificationType.ERROR);
      } finally {
        this.loading = false;
      }
    },
    //---------------------------------------- Local Function ------------------------------------------------------//
    async saveVdoSetTopic(payload) {
      let result;
      if (!payload.id) {
        result = await this.addVdoSetTopic(payload);
      } else {
        result = await this.updateVdoSetTopic(payload);
      }

      if (result) {
        this.updateListAfterSave(payload, result);
      }

    },
    async removeVdoSetTopic(payload) {
      let result = await this.deleteVdoSetTopic(payload);

      // remove list
      if (result) {
        this.updateListAfterDelete(result);
      }else{
        showLocalNotification(this.$vs, this.$t('general.warning')
          , this.$t('vdo-set-topic.can-not-delete')
          , notificationType.WARNING);
      }

    },
    async removeVdoSetTopicVideo(payload) {
      let result = await this.deleteOnlyVideo(payload);

      // remove list
      if (result) {
        this.updateListAfterSave(payload, result);
      }else{
        showLocalNotification(this.$vs, this.$t('general.warning')
          , this.$t('vdo-set-topic.can-not-delete')
          , notificationType.WARNING);
      }

    },
    // Bunny counterpart of removeVdoSetTopicVideo (Finding 6). The response
    // only carries the Bunny TOPIC_FIELDS subset plus vdoSet/vdoSetUnit
    // totalVdoLength (added for Finding 2) -- not the full topic shape --
    // so the topic itself is applied through applyBunnyTopic, same as
    // onBunnyRefresh/onBunnyCancel, rather than updateListAfterSave, which
    // expects the full shape and would throw on the missing fields. The
    // totals are still refreshed explicitly below (Finding 2): a deleted
    // video's vdoLength goes to 0 server-side and the backend recomputes
    // VdoSetUnit/VdoSet totalVdoLength to match, but nothing on this screen
    // was reading the new numbers back into the displayed unit/set totals
    // until now, so the mirror-image gap on the Wowza side
    // (removeVdoSetTopicVideo -> updateListAfterSave -> setTotalVdoLength)
    // is closed here too.
    async removeBunnyVideo(payload) {
      const topic = this.vdoSetTopicEdit.selectedVdoSetTopic;
      const updated = await this.deleteBunnyVideo(payload.id);

      if (updated) {
        // Finding 5: apply to both the selection and its entry in the unit's
        // topic list -- same two targets startBunnyPolling resolves on every
        // tick above. Applying only to the selection (as this used to) left
        // the list row still showing the deleted video's length until the
        // admin navigated away from the unit and back.
        const targets = [];
        if (topic) targets.push(topic);
        const list = this.vdoSetUnitEdit.selectedVdoSetUnit
          ? this.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics
          : [];
        const listEntry = list.find(item => item.id === payload.id);
        if (listEntry && !targets.includes(listEntry)) targets.push(listEntry);
        targets.forEach(target => this.applyBunnyTopic(target, updated));

        this.setTotalVdoLength(updated);
        showLocalNotification(this.$vs, this.$t('general.success')
          , this.$t('notification.delete-item-success-message', {value: updated.nameLocal})
          , notificationType.SUCCESS);
      } else {
        showLocalNotification(this.$vs, this.$t('general.warning')
          , this.$t('vdo-set-topic.can-not-delete')
          , notificationType.WARNING);
      }
    },
    async updateVdoLength() {
      // vdoStreamPath is a Wowza playback path (Finding 4). Sending the local
      // file name here -- as the Wowza screen's onUploadSuccess used to send
      // the server-returned path -- would make Preview/Download/Delete-Video/
      // Check-corrupt render for a Bunny topic and build a Wowza URL that
      // does not exist, and would get this topic picked up by the Wowza
      // size-migration job in get-vdo-path.js. Phase 2 defines the Bunny
      // playback reference; until then this field stays null for Bunny
      // topics.
      let payload = {
        id: this.vdoSetTopicEdit.selectedVdoSetTopic.id,
        teacherId: this.vdoSetEdit.courseOutline.teacher.id,
        vdoSetTopicInput: {
          vdoLength: parseInt(this.uploadPayload.vdoLength.toFixed(0)),
        }
      };
      let result = await this.updateVdoSetTopic(payload);

      if (result) {
        this.updateListAfterSave(payload, result);
      }
    },
    async updateAfterUploadThumbnail(thumbnail){
      let payload = {
        id: this.vdoSetTopicEdit.selectedVdoSetTopic.id,
        teacherId: this.vdoSetEdit.courseOutline.teacher.id,
        vdoSetTopicInput: {
          thumbnail: thumbnail,
        }
      };
      let result = await this.updateOnlyVdoSetTopic(payload);

      if (result) {
        this.form.vdoSetTopicInput.thumbnail = thumbnail;
        this.updateListAfterSave(payload, result);
      }
    },
    // Mirrors VdoSetTopicEdit.vue's method of the same name -- see the comment
    // there. vdoLength is not an editable field on this form either: it is
    // written by the upload path, by syncFromBunny, and by
    // borrowVdoSetTopicVideo. Sending the form's stale copy back is what
    // silently zeroed borrowed topics.
    measuredVdoLength() {
      return this.uploadPayload.vdoLength > 0 ? this.uploadPayload.vdoLength : null;
    },
    prepareDataBeforeSave() {
      delete this.form.vdoSetTopicInput.vdoStreamPath;

      let payload = {
        teacherId: this.vdoSetEdit.courseOutline.teacher.id,
        //set vdo duration here -- only when THIS save carries a new upload
        vdoSetTopicInput: withVdoLength(this.form.vdoSetTopicInput, this.measuredVdoLength()),
      };
      if (this.vdoSetTopicEdit.selectedVdoSetTopic && this.vdoSetTopicEdit.selectedVdoSetTopic.id) {
        //edit
        payload['id'] = this.vdoSetTopicEdit.selectedVdoSetTopic.id;
      } else {
        //insert
        payload['vdoSetId'] = this.vdoSetInfo.selectedVdoSet.id;
        payload['vdoSetUnitId'] = this.vdoSetUnitEdit.selectedVdoSetUnit.id;
        payload['courseOutlineId'] = this.vdoSetEdit.courseOutline.id;
      }

      return payload
    },
    setVdoLengthLabel(vdoLength) {
      let totalVdoLength = '00:00:00';
      if (vdoLength) {
        totalVdoLength = secondToHHMMSSFormat(vdoLength);
      }
      return totalVdoLength;
    },
    setSelectedClass(item) {
      let selectedClass = '';
      if (this.vdoSetTopicEdit.selectedVdoSetTopic && item.id === this.vdoSetTopicEdit.selectedVdoSetTopic.id) {
        return 'text-primary'
      }
      return selectedClass;
    },
    updateListAfterSave(payload, result) {
      //deep copy so we can mutate data
      result = JSON.parse(JSON.stringify(result));
      if (!payload.id) {
        //add new vdoSetTopic to vdoSetTopics list
        this.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics.push(result);

        //add new vdoSetUnit to vdoSets list
        const index = this.vdoSetInfo.selectedVdoSet.vdoSetUnits.findIndex(item => {
          return item.id === this.vdoSetUnitEdit.selectedVdoSetUnit.id
        });
        this.vdoSetInfo.selectedVdoSet.vdoSetUnits[index] = this.vdoSetUnitEdit.selectedVdoSetUnit

        this.onAddClick();
      } else {
        //update new vdoSetTopic to vdoSetUnis list
        const index = this.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics.findIndex(item => {
          return item.id === result.id
        });
        // $set rather than [index] =: Vue 2 cannot observe an assignment made
        // through an array index, so the left-hand list row kept rendering the
        // values it was loaded with -- a renamed topic still showed its old
        // name until the unit was reopened.
        this.$set(this.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics, index, result);

        //add new vdoSetUnit to vdoSets list
        const indexUnit = this.vdoSetInfo.selectedVdoSet.vdoSetUnits.findIndex(item => {
          return item.id === this.vdoSetUnitEdit.selectedVdoSetUnit.id
        });
        this.vdoSetInfo.selectedVdoSet.vdoSetUnits[indexUnit] = this.vdoSetUnitEdit.selectedVdoSetUnit;

        this.vdoSetTopicEdit.selectedVdoSetTopic = result;
      }
      this.setTotalVdoLength(result);

      // Every caller of this method -- the form save, updateVdoLength after an
      // upload, and the thumbnail upload -- has just swapped the selected topic
      // for a fresh object built from a mutation response, which silently
      // re-dates the Bunny fields the video panel renders from. That is how
      // ticking "ใช้งาน" mid-encode came to look like it had finished the
      // video. Re-arming here puts the status back under its only owner:
      // Bunny, read through refreshBunnyStatus. A reading that is momentarily
      // behind gets corrected within one round trip, and the mutation cannot
      // write these fields at all -- VdoSetTopicInput deliberately excludes
      // videoProvider, bunnyVideoGuid and bunnyStatus (see the input type in
      // studx-admin-backend/src/schemas/vdoSet/vdoSetTopic.graphql).
      //
      // On the post-upload path this and onSuccess's own startBunnyPolling
      // both fire. onSuccess wins the race -- await updateVdoLength() resolves
      // on this method's synchronous body and does not wait for the
      // un-awaited call below -- so it arms first and the call below replaces
      // it one round trip later. Both target the same topic and guid, and
      // startBunnyPolling stops any existing timer before arming, so the only
      // effect is one extra status read: the read that makes the panel correct
      // immediately rather than ten seconds later.
      this.maybeStartPollingFor(this.vdoSetTopicEdit.selectedVdoSetTopic);
    },
    updateListAfterDelete(result) {
      // The topic this loop polls is about to stop existing, and
      // refreshBunnyStatus throws for an id the backend can no longer find.
      // Same reasoning as onBunnyCancel: stop before the row goes.
      this.stopBunnyPolling();

      //remove from list
      const index = this.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics.findIndex(item => item.id === result.id);
      this.vdoSetUnitEdit.selectedVdoSetUnit.vdoSetTopics.splice(index, 1);

      setEmptyVdoSetTopicForm();
      this.setTotalVdoLength(result);

    },
    setTotalVdoLength(result) {
      this.vdoSetUnitEdit.selectedVdoSetUnit.totalVdoLength = result.vdoSetUnit.totalVdoLength;
      this.vdoSetInfo.selectedVdoSet.totalVdoLength = result.vdoSet.totalVdoLength;
    },
    setVdoInfo(file, uploadPayload) {
      return new Promise((resolve) => {
        //set upload file name
        let fileExtension = file.name.split('.').pop();
        uploadPayload.fileName = file.name;
        uploadPayload.fileNameUpload = config.prefixFileName.vdoOnDemand
          + '_' + this.vdoSetEdit.courseOutline.subject.code
          + '_' + this.vdoSetEdit.courseOutline.teacher.code
          + '_' + this.vdoSetUnitEdit.selectedVdoSetUnit.unitSeq
          + '_' + this.vdoSetTopicEdit.selectedVdoSetTopic.no
          + '_' + this.vdoSetTopicEdit.selectedVdoSetTopic.id
          + '.' + fileExtension

        //set video info
        let video = document.createElement('video');
        video.src = URL.createObjectURL(file);
        video.preload = 'metadata';
        video.onloadedmetadata = function () {
          window.URL.revokeObjectURL(video.src);
          uploadPayload.vdoLength = video.duration;
          resolve(true);
        }
        // A file the browser cannot decode never fires onloadedmetadata --
        // without this the promise would never settle, leaving the caller
        // stuck with local.isUploading already true and the panel showing
        // 0% forever. vdoLength is left at whatever it already was: writing
        // a bogus value here would feed a wrong duration into an upload the
        // caller is about to abort anyway.
        video.onerror = function () {
          window.URL.revokeObjectURL(video.src);
          resolve(false);
        }
      });
    },
    clearVideoUploadPayload() {
      this.uploadPayload.fileName = '';
      this.uploadPayload.fileNameUpload = '';
      this.uploadPayload.vdoLength = 0;
    },
    setTabDefaultPosition(){
      this.selectedTab = 0;
    },
    // async computeFileChecksum(file) {
    //   const objType = Object.prototype.toString.call(file);
    //   // console.log('objType', objType);
    //   const reader = new FileReader();
    //   reader.readAsArrayBuffer(file);
    //   reader.onloadend = function () {
    //     // console.log('onloadend')
    //     const hash = crypto.createHash('md5');
    //     hash.update(new Uint8Array(reader.result));
    //     const checksum = hash.digest('hex');
    //     // console.log('checksum', checksum);
    //     return hash.digest('hex');
    //   };
    //
    //   // const buffer = await file.arrayBuffer();
    //   // const hash = crypto.createHash('md5');
    //   // hash.update(new Uint8Array(buffer));
    //   // return hash.digest('hex');
    // }
    async loadSharing() {
      const topic = this.currentVdoSetTopic;
      if (!topic || !topic.id) {
        this.sharing = null;
        return;
      }
      this.sharing = await this.getVdoSetTopicSharing({id: topic.id});
    },
    async onBorrowSelected(sourceTopic) {
      const target = this.currentVdoSetTopic;
      if (!target || !target.id) return;

      this.loading = true;
      try {
        const result = await this.borrowVdoSetTopicVideo({
          targetVdoSetTopicId: target.id,
          sourceVdoSetTopicId: sourceTopic.id,
        });
        // A falsy result means the action hit a GraphQL error and already
        // committed it -- the error watcher raises that notification, so
        // there is nothing to do here but skip the list update.
        if (result) {
          // updateListAfterSave(payload, result) -- it only reads payload.id, to
          // tell an insert from an update. Borrowing always targets an existing
          // topic, so the id is always present and it takes the update branch.
          this.updateListAfterSave({id: target.id}, result);
          await this.loadSharing();
        }
      } finally {
        this.loading = false;
      }
    },
    // The extra sentence a Delete confirmation needs when the file is shared:
    // without it an admin reads "delete" as "the video is gone", which for a
    // shared file is not what happens.
    sharedDeleteMessage() {
      const count = this.sharing && this.sharing.videoSharedWith ?
        this.sharing.videoSharedWith.length : 0;
      if (!count) return null;
      return this.$t('video-reuse.delete-shared-warning', {count});
    },
  },
  beforeDestroy() {
    this.stopBunnyPolling();
    if (this.bunny.upload) this.bunny.upload.abort();
  },
}
</script>

<style scoped>

</style>
